import { useCallback, useEffect, useRef } from 'react';

import { usePlatformService } from 'core';
import { useUserData } from 'core/Api';
import { useGrowthBook } from 'core/Packages';
import { useLocalStorage } from 'ui/Hooks';

import { useUpdateExtensionStatus } from '../../hooks/queries/Core/users';
import useGtmServices from '../../hooks/useGtmServices';
import chromeExtension from '../../utils/chromeExtension';

// Interim solution to provide user listen functionality without forcing the rest of the app to listen to the entirety of the user object.
function UserWatcher() {
  const { data: user } = useUserData();
  const platform = usePlatformService();

  const [segmentAnonId] = useLocalStorage<string | undefined>({
    key: 'ajs_anonymous_id',
    defaultValue: undefined,
  });

  const { mutate: updateExtensionStatus } = useUpdateExtensionStatus();
  const GtmService = useGtmServices();
  const growthbook = useGrowthBook();
  const isPendoInitializedRef = useRef(false);

  /** Handle Pendo User */
  const setPendoUser = useCallback(() => {
    const visitor = user
      ? {
          id: user.uuid,
          full_name: `${user.first_name} ${user.last_name}`,
          email: user.email,
          first_name: user.first_name,
          last_name: user.last_name,
          device_type: platform.isNative ? 'native' : 'web',
        }
      : {
          device_type: platform.isNative ? 'native' : 'web',
        };

    // If not inialized, initialize pendo with the visitor object else update the visitor object
    if (!isPendoInitializedRef.current) {
      window?.pendo?.initialize?.({ visitor: visitor });
      isPendoInitializedRef.current = true;
    } else {
      window?.pendo?.updateOptions?.({ visitor: visitor });
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      GtmService.updateUser();

      chromeExtension.setUser(user).catch(() => {});

      chromeExtension
        .isInstalled()
        .then((res: any) => {
          if (res) {
            updateExtensionStatus({ platform: 'Chrome', status: true });
          }
        })
        .catch(() => {
          updateExtensionStatus({ platform: 'Chrome', status: false });
        });
    }
  }, [user?.uuid]);

  useEffect(() => {
    setPendoUser();
    return () => {};
  }, [setPendoUser, user?.uuid]);

  useEffect(() => {
    if (!growthbook) return;

    if (user) {
      growthbook.setAttributes({
        ...user,
        // Sets employee attribute to true if user is internal
        // This can be used for Growthbook experiments to only feature flag things for
        // internal employees
        employee: user?.internal ?? false,
        // Set the growthbook user id to the user uuid as it's a unique identifier
        id: user?.uuid,
      });
    } else {
      // Utilize the segment anonymous id if the user is not logged in
      growthbook.setAttributes({
        // Set the growthbook user id to the user uuid as it's a unique identifier
        id: segmentAnonId,
      });
    }
  }, [user, growthbook, segmentAnonId]);

  return <></>;
}

export { UserWatcher };
